import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Globe, DollarSign, ShoppingBag, Mail, ArrowRight, TrendingUp,
  Loader2, Eye, Users as UsersIcon,
} from 'lucide-react';
import {
  GlassCard, StatCardV2, DashboardBackdrop, GradientAreaChart,
} from '@/components/dashboard/dashboard-ui';
import AnalyticsChartsBlock from './AnalyticsChartsBlock';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function VisitsBlock({ days = 7 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('masterToken');
        const res = await fetch(`${API}/site-visits/master/summary?days=${days}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setData(await res.json());
      } catch {/* ignore */} finally { setLoading(false); }
    };
    load();
  }, [days]);

  const chartData = useMemo(() => {
    if (!data?.series?.length) return [];
    return data.series.map((d) => ({
      name: d.day?.slice(5) || '',
      visits: d.total_visits || 0,
      unique: d.unique_visitors || 0,
    }));
  }, [data]);

  if (loading) {
    return (
      <div className="h-48 flex items-center justify-center">
        <Loader2 size={20} className="animate-spin text-[#A78BFA]" />
      </div>
    );
  }
  if (!chartData.length) {
    return (
      <div className="h-48 flex items-center justify-center text-sm text-[#7B7FA8]">
        No visit data yet
      </div>
    );
  }

  return (
    <div data-testid="visits-chart">
      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="p-4 rounded-xl bg-white/[0.035] border border-white/[0.06]">
          <p className="text-[10px] text-[#A78BFA] uppercase tracking-[0.18em] font-bold">
            Total Visits · {days}d
          </p>
          <p className="text-2xl font-bold text-white mt-1 font-syne tabular-nums">
            {data.totals.total_visits.toLocaleString()}
          </p>
        </div>
        <div className="p-4 rounded-xl bg-white/[0.035] border border-white/[0.06]">
          <p className="text-[10px] text-cyan-300 uppercase tracking-[0.18em] font-bold">
            Unique Visitors · {days}d
          </p>
          <p className="text-2xl font-bold text-white mt-1 font-syne tabular-nums">
            {data.totals.unique_visitors.toLocaleString()}
          </p>
        </div>
      </div>
      <GradientAreaChart
        data={chartData}
        height={180}
        series={[
          { key: 'visits', label: 'Total', ramp: 'violet' },
          { key: 'unique', label: 'Unique', ramp: 'cyan'   },
        ]}
        yFormatter={(v) => Number(v).toLocaleString()}
      />
    </div>
  );
}

export default function MasterHome() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('masterToken');
        const response = await axios.get(`${API}/master/analytics`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAnalytics(response.data);
      } catch (error) {
        console.error('Failed to fetch analytics:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={32} className="animate-spin text-[#8B5CF6]" />
      </div>
    );
  }

  return (
    <div className="relative" data-testid="master-home">
      <DashboardBackdrop />
      <div className="relative z-10 space-y-8">
        {/* Header */}
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#A78BFA] font-bold mb-2">
            Platform Control
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold text-white font-syne tracking-tight" data-testid="master-title">
            Master Dashboard
          </h1>
          <p className="text-[#A5A8C9] text-sm mt-1.5">
            A bird's-eye view of every site, sale, and signup on EntrepreNatives.
          </p>
        </div>

        {/* Stats Grid — 4 glass cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="master-stats">
          <StatCardV2
            icon={Globe}
            label="Total Sites"
            value={analytics?.total_sites || 0}
            ramp="cyan"
            spark={[2, 4, 3, 6, 5, 8, 7, 10].map((y, x) => ({ x, y }))}
            sparkId="msites"
            data-testid="master-stat-total-sites"
            delay={0}
          />
          <StatCardV2
            icon={DollarSign}
            label="Total Revenue"
            value={analytics?.total_sales || 0}
            prefix="$"
            decimals={2}
            ramp="lime"
            spark={[3, 5, 4, 8, 6, 11, 9, 14].map((y, x) => ({ x, y }))}
            sparkId="mrev"
            data-testid="master-stat-total-revenue"
            delay={0.08}
          />
          <StatCardV2
            icon={ShoppingBag}
            label="Total Orders"
            value={analytics?.total_orders || 0}
            ramp="amber"
            spark={[1, 3, 2, 5, 4, 7, 6, 9].map((y, x) => ({ x, y }))}
            sparkId="mord"
            data-testid="master-stat-total-orders"
            delay={0.16}
          />
          <StatCardV2
            icon={Mail}
            label="Total Signups"
            value={analytics?.total_signups || 0}
            ramp="fuchsia"
            spark={[2, 3, 3, 5, 4, 7, 6, 10].map((y, x) => ({ x, y }))}
            sparkId="msig"
            data-testid="master-stat-total-signups"
            delay={0.24}
          />
        </div>

        {/* Two-column: Site Traffic + Sites Performance */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Site-wide Traffic */}
          <GlassCard glow className="lg:col-span-2 p-6" data-testid="site-traffic-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-[11px] uppercase tracking-[0.18em] text-[#A78BFA] font-bold mb-1">
                  Activity
                </p>
                <h3 className="text-lg font-bold text-white font-syne flex items-center gap-2">
                  <Eye size={16} className="text-[#A78BFA]" /> Site Traffic · last 7 days
                </h3>
              </div>
              <span className="text-[10px] uppercase tracking-wider text-[#7B7FA8]">
                Anonymized · privacy-safe
              </span>
            </div>
            <VisitsBlock days={7} />
          </GlassCard>

          {/* Quick Actions stacked */}
          <div className="space-y-5 flex flex-col">
            <Link to="/master/sites/new" data-testid="quick-create-site" className="block">
              <GlassCard glow className="p-5 hover:border-[#8B5CF6]/35 cursor-pointer group transition-all">
                <div className="flex items-center gap-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                    style={{
                      background: 'linear-gradient(135deg, #8B5CF6, #6D28D9)',
                      boxShadow: '0 10px 24px -8px rgba(139,92,246,0.55)',
                    }}
                  >
                    <Globe size={22} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-white text-sm font-syne">Create new site</h3>
                    <p className="text-xs text-[#A5A8C9] mt-0.5">Add a new client website</p>
                  </div>
                  <ArrowRight size={16} className="text-[#7B7FA8] group-hover:text-white group-hover:translate-x-0.5 transition-all" />
                </div>
              </GlassCard>
            </Link>
            <Link to="/master/users" data-testid="quick-users" className="block">
              <GlassCard glow className="p-5 hover:border-cyan-400/30 cursor-pointer group transition-all">
                <div className="flex items-center gap-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                    style={{
                      background: 'linear-gradient(135deg, #22D3EE, #0EA5E9)',
                      boxShadow: '0 10px 24px -8px rgba(34,211,238,0.55)',
                    }}
                  >
                    <UsersIcon size={22} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-white text-sm font-syne">Manage users</h3>
                    <p className="text-xs text-[#A5A8C9] mt-0.5">Vendors, consumers, admins</p>
                  </div>
                  <ArrowRight size={16} className="text-[#7B7FA8] group-hover:text-white group-hover:translate-x-0.5 transition-all" />
                </div>
              </GlassCard>
            </Link>
            <Link to="/master/billing" data-testid="quick-billing" className="block">
              <GlassCard glow className="p-5 hover:border-fuchsia-400/30 cursor-pointer group transition-all">
                <div className="flex items-center gap-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                    style={{
                      background: 'linear-gradient(135deg, #EC4899, #A21CAF)',
                      boxShadow: '0 10px 24px -8px rgba(236,72,153,0.55)',
                    }}
                  >
                    <TrendingUp size={22} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-white text-sm font-syne">Billing & plans</h3>
                    <p className="text-xs text-[#A5A8C9] mt-0.5">Subscriptions, trials, overrides</p>
                  </div>
                  <ArrowRight size={16} className="text-[#7B7FA8] group-hover:text-white group-hover:translate-x-0.5 transition-all" />
                </div>
              </GlassCard>
            </Link>
          </div>
        </div>

        {/* Sites Performance — by-country Vision UI pattern */}
        <GlassCard glow className="overflow-hidden" data-testid="sites-performance">
          <div className="flex items-center justify-between px-6 py-5 border-b border-white/[0.06]">
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-[#A78BFA] font-bold mb-1">
                Performance
              </p>
              <h3 className="text-lg font-bold text-white font-syne">Sites Performance</h3>
            </div>
            <Link to="/master/sites" className="text-sm text-[#A78BFA] hover:text-white flex items-center gap-1 transition-colors">
              View All <ArrowRight size={14} />
            </Link>
          </div>

          {analytics?.sites?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr>
                    <th className="px-6 pb-3 pt-4 text-[10px] uppercase tracking-[0.16em] text-[#7B7FA8] font-semibold">Site</th>
                    <th className="px-6 pb-3 pt-4 text-[10px] uppercase tracking-[0.16em] text-[#7B7FA8] font-semibold text-right">Revenue</th>
                    <th className="px-6 pb-3 pt-4 text-[10px] uppercase tracking-[0.16em] text-[#7B7FA8] font-semibold text-right">Orders</th>
                    <th className="px-6 pb-3 pt-4 text-[10px] uppercase tracking-[0.16em] text-[#7B7FA8] font-semibold text-right">Signups</th>
                    <th className="px-6 pb-3 pt-4 text-[10px] uppercase tracking-[0.16em] text-[#7B7FA8] font-semibold text-right">Avg Order</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.sites.map((site) => (
                    <tr
                      key={site.site_id}
                      className="border-t border-white/[0.04] hover:bg-white/[0.025] transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-9 h-9 rounded-lg flex items-center justify-center text-[11px] font-bold text-white shrink-0"
                            style={{
                              background: 'linear-gradient(135deg, #8B5CF6, #EC4899)',
                              boxShadow: '0 6px 18px -6px rgba(139,92,246,0.55)',
                            }}
                          >
                            {(site.name || '?').slice(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-semibold text-white">{site.name}</p>
                            <p className="text-xs text-[#7B7FA8]">{site.domain}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right font-semibold text-[#A3E635] tabular-nums">
                        ${Number(site.sales || 0).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 text-right text-[#A5A8C9] tabular-nums">{site.orders}</td>
                      <td className="px-6 py-4 text-right text-[#A5A8C9] tabular-nums">{site.signups}</td>
                      <td className="px-6 py-4 text-right text-[#A5A8C9] tabular-nums">
                        ${site.orders > 0 ? (site.sales / site.orders).toFixed(2) : '0.00'}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-white/10 bg-white/[0.02]">
                    <td className="px-6 pt-4 pb-3 font-bold text-white">Total</td>
                    <td className="px-6 pt-4 pb-3 text-right text-[#A3E635] font-bold tabular-nums">${Number(analytics.total_sales || 0).toFixed(2)}</td>
                    <td className="px-6 pt-4 pb-3 text-right text-white font-bold tabular-nums">{analytics.total_orders || 0}</td>
                    <td className="px-6 pt-4 pb-3 text-right text-white font-bold tabular-nums">{analytics.total_signups || 0}</td>
                    <td className="px-6 pt-4 pb-3 text-right text-white font-bold tabular-nums">
                      ${analytics.total_orders > 0 ? (analytics.total_sales / analytics.total_orders).toFixed(2) : '0.00'}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="text-center py-16 px-6">
              <Globe size={48} className="mx-auto text-[#5A5E80] mb-4" />
              <p className="text-[#A5A8C9]">No sites yet</p>
              <Link
                to="/master/sites/new"
                className="inline-block mt-4 px-6 py-2.5 rounded-xl text-sm font-bold uppercase tracking-[0.16em] bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white shadow-[0_8px_24px_rgba(139,92,246,0.35)] hover:shadow-[0_8px_30px_rgba(139,92,246,0.55)] transition-all"
              >
                Create Your First Site
              </Link>
            </div>
          )}
        </GlassCard>

        {/* Iter 175g — Analytics merged into Overview. Revenue donut + Orders/Signups
            bar chart used to live on /master/analytics; now they render here so the
            master only needs one tab to see all platform performance. */}
        <AnalyticsChartsBlock analytics={analytics} />
      </div>
    </div>
  );
}
